/**
 * Password validation utility
 * Requirements:
 * - Minimum 8 characters
 * - At least 1 letter
 * - At least 1 number
 * - Block very common/weak passwords
 */

// Common weak passwords to block
const WEAK_PASSWORDS = [
  "password",
  "123456",
  "12345678",
  "123456789",
  "1234567890",
  "qwerty",
  "abc123",
  "password123",
  "admin",
  "letmein",
  "welcome",
  "monkey",
  "1234567",
  "sunshine",
  "princess",
  "football",
  "iloveyou",
  "123123",
  "dragon",
  "trustno1",
];

export type PasswordValidationResult = {
  isValid: boolean;
  errors: string[];
};

/**
 * Validate password against all rules
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  if (!password) {
    return {
      isValid: false,
      errors: ["Password is required"],
    };
  }

  // Check minimum length
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  // Check for at least one letter
  if (!/[a-zA-Z]/.test(password)) {
    errors.push("Password must include at least one letter");
  }

  // Check for at least one number
  if (!/[0-9]/.test(password)) {
    errors.push("Password must include at least one number");
  }

  // Check against weak passwords (case-insensitive)
  const passwordLower = password.toLowerCase();
  if (WEAK_PASSWORDS.includes(passwordLower)) {
    errors.push("This password is too common. Please choose a stronger password");
  }

  // Also check if password is just a weak password with common patterns appended
  // e.g., "password123!", "Review123!"
  const commonPatterns = [
    /^password\d+[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/i,
    /^review\d+[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/i,
    /^\d+password[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/i,
  ];

  if (commonPatterns.some(pattern => pattern.test(password))) {
    errors.push("This password is too predictable. Please choose a stronger password");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get a user-friendly error message summarizing what's missing
 */
export function getPasswordErrorMessage(password: string): string | null {
  const result = validatePassword(password);
  if (result.isValid) {
    return null;
  }

  // Return the first error message
  return result.errors[0] || "Password does not meet requirements";
}

/**
 * Check individual password requirements (for UI feedback)
 */
export type PasswordRequirement = {
  label: string;
  met: boolean;
};

export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    {
      label: "At least 8 characters",
      met: password.length >= 8,
    },
    {
      label: "At least one letter",
      met: /[a-zA-Z]/.test(password),
    },
    {
      label: "At least one number",
      met: /[0-9]/.test(password),
    },
  ];
}

